/**
 * Code custom Navibreizhshop.
 *
 * Enregistre la fourchette de livraison affichée sous chaque article du panier
 * comme propriété de la ligne (« Livraison estimée »). Shopify l'affiche alors
 * tout seul sous l'article dans le récapitulatif de la page de paiement, dans
 * la commande, et elle peut être reprise dans les e-mails.
 *
 * Fonctionne avec snippets/cart-line-delivery-estimation.liquid, qui rend un
 * élément <cart-delivery-property> par ligne. La propriété est mise à jour à
 * chaque affichage du panier, uniquement si la date a changé.
 */

const MAX_ATTEMPTS = 2;
const MAX_WAITS = 3;
const attempts = new Map();
let queue = Promise.resolve();

function lowerFirst(text) {
  return text ? text.charAt(0).toLowerCase() + text.slice(1) : text;
}

function cartChangeUrl() {
  if (typeof Theme !== 'undefined' && Theme.routes?.cart_change_url) {
    return Theme.routes.cart_change_url;
  }

  return `${window.Shopify?.routes?.root || '/'}cart/change.js`;
}

class CartDeliveryProperty extends HTMLElement {
  #waits = 0;

  connectedCallback() {
    if (window.Shopify?.designMode) return;
    if (!this.dataset.lineKey || !this.dataset.propertyName) return;

    customElements.whenDefined('delivery-estimation').then(() => {
      requestAnimationFrame(() => this.#sync());
    });
  }

  /**
   * Texte attendu pour la propriété, construit à partir des dates affichées
   */
  #expectedValue() {
    const estimation = this.parentElement?.querySelector('delivery-estimation');
    const min = estimation?.querySelector('[data-ref="min-date"]')?.textContent.trim();
    const max = estimation?.querySelector('[data-ref="max-date"]')?.textContent.trim();

    if (!min || !max) return '';

    return (this.dataset.template || '[min] - [max]')
      .replace('[min]', lowerFirst(min))
      .replace('[max]', lowerFirst(max));
  }

  /**
   * Propriétés actuelles de la ligne (à renvoyer en entier : Shopify remplace
   * tout l'objet à chaque modification)
   */
  #currentProperties() {
    try {
      const parsed = JSON.parse(this.dataset.properties || '{}');

      if (Array.isArray(parsed)) return Object.fromEntries(parsed);

      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return null;
    }
  }

  #sync() {
    if (!this.isConnected) return;

    const expected = this.#expectedValue();

    if (!expected) {
      // Dates pas encore affichées : on réessaie un peu plus tard
      if (this.#waits < MAX_WAITS) {
        this.#waits += 1;
        setTimeout(() => this.#sync(), 400);
      }
      return;
    }

    if (expected === (this.dataset.currentValue || '')) return;

    const properties = this.#currentProperties();
    if (!properties) return;

    const key = this.dataset.lineKey;
    const signature = `${key}|${expected}`;
    const count = attempts.get(signature) || 0;
    if (count >= MAX_ATTEMPTS) return;
    attempts.set(signature, count + 1);

    properties[this.dataset.propertyName] = expected;

    queue = queue
      .then(() => {
        // Le panier a été rechargé entre-temps : le nouvel élément s'en charge
        if (!this.isConnected) return null;

        return fetch(cartChangeUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, properties }),
        }).then((response) => {
          if (response.ok) this.dataset.currentValue = expected;
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }
}

if (!customElements.get('cart-delivery-property')) {
  customElements.define('cart-delivery-property', CartDeliveryProperty);
}
